import {
  type IssueDetail,
  type IssueNextAction,
  ISSUE_TITLE_MAX_LENGTH,
} from '@cortex/domain-ac-mens-hub-api';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import {
  formatShortDate,
  ISSUE_PRIORITY_LABEL_KEYS,
  ISSUE_REJECT_REASON_LABEL_KEYS,
} from '../../lib/issue-mgmt-format.js';
import {
  nextActionButton,
  nextActionLabel,
  type NextActionTarget,
  waitingLabel,
} from '../../lib/issue-mgmt-next-action.js';
import { InlineTextEditor } from './InlineTextEditor.js';
import { IssueStatusBadge } from './IssueStatusBadge.js';
import type { SaveIssueFields } from './useIssueFieldSave.js';

/**
 * 課題詳細の見出しで押せる操作。
 * @graph-connects none
 */
export interface IssueHeaderActions {
  /** 判定の小窓を開く（fill は「対応する」を今の値つきで開く） */
  onDecide: (mode: 'change' | 'fill') => void;
  /** 「対応した」の小窓を開く */
  onDone: () => void;
  /** 「再発した」を記録する */
  onReopen: () => void;
  /** 案件を紐づける小窓を開く */
  onLinkCase: () => void;
  /** 「返信済みにする」をその場で記録する */
  onReply: () => void;
  /** 「Backlogで確かめた」をその場で記録する */
  onCheckCase: (caseKey: string) => void;
}

/**
 * やることの該当カードの URL（判定・直ったかは中身と声を見て決めるため、カードへ移る）。
 * @graph-connects none
 */
function cardSearch(issueId: string, next: IssueNextAction): { card: string } {
  return { card: `issue:${issueId}:${next.kind}` };
}

/**
 * 次にやることの箱（文言・誰が・期限切れなら赤・大ボタン・ほかN件、無ければ待ちの様子と小さい操作）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題詳細の右に、次にやることの文言と受け持つ人と大ボタン1つを出し、無ければ待ちの様子と小さい操作（対応した・再発した）を出す
 * @graph-connects nextActionLabel [calls] 次にやることの文言
 * @graph-connects nextActionButton [calls] 大ボタンの行き先
 * @graph-connects waitingLabel [calls] 待ちの様子
 */
function NextBox({
  issue,
  canEdit,
  actions,
  now,
}: {
  issue: IssueDetail;
  canEdit: boolean;
  actions: IssueHeaderActions;
  now: Date;
}) {
  const { t } = useTranslation();
  const next = issue.next;
  const smallDone =
    next === null &&
    issue.verdict === 'accepted' &&
    issue.devRequired === false &&
    issue.manualProgress !== 'done' &&
    issue.resolvedAt === null;
  const smallReopen = next === null && issue.verdict === 'accepted' && issue.resolvedAt !== null;
  const button = next === null ? null : nextActionButton(next, t);
  const pressTarget = button === null || button.target === 'todo' ? null : button.target;

  /** カードへ移る以外の大ボタン（小窓を開く・その場で記録する）。 */
  const press = (target: Exclude<NextActionTarget, 'todo'>, current: IssueNextAction) => {
    const handlers: Record<Exclude<NextActionTarget, 'todo'>, () => void> = {
      decide: () => {
        actions.onDecide('fill');
      },
      link_case: actions.onLinkCase,
      done: actions.onDone,
      reply: actions.onReply,
      check_case: () => {
        if (current.kind === 'check_case') actions.onCheckCase(current.caseKey);
      },
    };
    handlers[target]();
  };

  return (
    <div className="w-full shrink-0 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-4 md:w-72">
      <p className="text-xs text-[var(--color-text-muted)]">{t('issueMgmt.next.heading')}</p>
      {next === null ? (
        <p className="mt-1 text-sm font-medium text-[var(--color-text-secondary)]">
          {waitingLabel(issue.waiting, t, (date) => formatShortDate(date, now))}
        </p>
      ) : (
        <p
          className={`mt-1 text-sm font-medium ${next.overdue ? 'text-[var(--color-error)]' : 'text-[var(--color-text-primary)]'}`}
        >
          {nextActionLabel(next, t)}
          <span className="ml-1 text-xs">
            {t('issueMgmt.next.who', {
              name: next.who?.displayName ?? t('issueMgmt.todo.noOwner'),
            })}
            {next.overdue && t('issueMgmt.next.overdueMark')}
          </span>
        </p>
      )}
      {canEdit && next !== null && button !== null && (
        <div className="mt-3">
          {pressTarget === null ? (
            <Link
              to="/issue-mgmt/todo"
              search={cardSearch(issue.id, next)}
              className="block w-full rounded-lg bg-[var(--color-accent-primary)] px-4 py-2.5 text-center text-sm font-semibold text-white hover:opacity-90"
            >
              {button.label}
            </Link>
          ) : (
            <Button
              fullWidth
              size="lg"
              onClick={() => {
                press(pressTarget, next);
              }}
            >
              {button.label}
            </Button>
          )}
        </div>
      )}
      {next !== null && next.otherCount > 0 && (
        <p className="mt-2 text-xs text-[var(--color-text-muted)]">
          {t('issueMgmt.next.others', { n: String(next.otherCount) })}
        </p>
      )}
      {canEdit && smallDone && (
        <Button variant="secondary" size="sm" className="mt-3" onClick={actions.onDone}>
          {t('issueMgmt.next.markDone')}
        </Button>
      )}
      {canEdit && smallReopen && (
        <Button variant="secondary" size="sm" className="mt-3" onClick={actions.onReopen}>
          {t('issueMgmt.next.reopen')}
        </Button>
      )}
    </div>
  );
}

/**
 * 課題詳細の見出し（番号・タイトル・状態・担当・優先度・開発・見直す日か理由・Backlog の進み・判定を変える）と、
 * 右の「次にやること」の箱。タイトルは編集キーの人がその場で直せる。次にやることは判定表（API が決める）の文言と
 * 大ボタン1つで、判定・直ったかはやることのカードへ移り、必須項目・起案・対応したは小窓、返信済み・Backlogで確かめたは
 * その場で記録する。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題の見出しで状態と担当と Backlog の進みを見せ、次にやることの大ボタンを1つ出す
 * @graph-connects NextBox [calls] 次にやることの箱
 * @graph-connects InlineTextEditor [calls] タイトルをその場で直す
 */
export function IssueHeader({
  issue,
  canEdit,
  save,
  actions,
  caseProgress,
  now,
}: {
  issue: IssueDetail;
  canEdit: boolean;
  save: SaveIssueFields;
  actions: IssueHeaderActions;
  /** 紐づく案件の数と完了の数（案件欄を読み終えるまで null） */
  caseProgress: { total: number; completed: number } | null;
  now: Date;
}) {
  const { t } = useTranslation();
  const dash = t('issueMgmt.common.dash');
  const open = issue.verdict !== 'merged';

  return (
    <section className="flex flex-col gap-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-5 md:flex-row md:items-start">
      <div className="min-w-0 flex-1 space-y-2">
        <p className="text-xs font-semibold text-[var(--color-text-muted)]">{issue.id}</p>
        <InlineTextEditor
          label={t('issueMgmt.field.title')}
          value={issue.title}
          max={ISSUE_TITLE_MAX_LENGTH}
          canEdit={canEdit && open}
          multiline={false}
          required
          onSave={(next) =>
            save(
              [{ field: 'title', base: issue.title, value: next ?? '' }],
              t('issueMgmt.detail.titleSaved'),
            )
          }
        >
          <h2 className="text-xl font-bold text-[var(--color-text-primary)]">{issue.title}</h2>
        </InlineTextEditor>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[var(--color-text-secondary)]">
          {issue.displayStatus !== null && <IssueStatusBadge status={issue.displayStatus} />}
          <span>
            {t('issueMgmt.detail.assignee')} {issue.assignee?.displayName ?? dash}
            {issue.assignee !== null && !issue.assignee.hasEditKey && (
              <span className="text-xs text-[var(--color-error)]">
                {t('issueMgmt.detail.lostKey')}
              </span>
            )}
          </span>
          <span>
            {t('issueMgmt.detail.priority')}{' '}
            {issue.priority === null ? dash : t(ISSUE_PRIORITY_LABEL_KEYS[issue.priority])}
          </span>
          {issue.devRequired !== null && (
            <span>
              {t('issueMgmt.detail.devRequired')}{' '}
              {issue.devRequired ? t('issueMgmt.detail.devYes') : t('issueMgmt.detail.devNo')}
            </span>
          )}
          {issue.verdict === 'on_hold' && issue.holdUntil !== null && (
            <span>
              {t('issueMgmt.detail.holdUntil', { date: formatShortDate(issue.holdUntil, now) })}
            </span>
          )}
          {issue.verdict === 'rejected' && issue.rejectReason !== null && (
            <span>
              {t('issueMgmt.detail.rejectReason', {
                reason: t(ISSUE_REJECT_REASON_LABEL_KEYS[issue.rejectReason]),
              })}
            </span>
          )}
          {caseProgress !== null && caseProgress.total > 0 && (
            <span>
              {t('issueMgmt.cases.progress', {
                total: String(caseProgress.total),
                completed: String(caseProgress.completed),
              })}
            </span>
          )}
        </div>
        {canEdit && open && (
          <button
            type="button"
            onClick={() => {
              actions.onDecide('change');
            }}
            className="text-xs font-medium text-[var(--color-accent-primary)] hover:underline"
          >
            {t('issueMgmt.detail.changeVerdict')}
          </button>
        )}
      </div>
      {open && <NextBox issue={issue} canEdit={canEdit} actions={actions} now={now} />}
    </section>
  );
}
